"use client";

import type {
	Dispatch,
	MutableRefObject,
	PointerEvent,
	SetStateAction,
} from "react";
import { useCallback, useRef, useState } from "react";

function clamp01(n: number): number {
	return Math.min(1, Math.max(0, n));
}

/**
 * Pointer axis + sign for expand.
 * - `expand-on-drag-down` / `up`: vertical (TopDrawer / BottomDrawer)
 * - `expand-on-drag-right` / `left`: horizontal (future SideDrawer left/right)
 */
export type SnapDragGesture =
	| "expand-on-drag-down"
	| "expand-on-drag-up"
	| "expand-on-drag-right"
	| "expand-on-drag-left";

function clampSnapCommitThreshold(threshold: number): number {
	if (!Number.isFinite(threshold)) {
		return 0.5;
	}
	return Math.min(0.5, Math.max(0.02, threshold));
}

/**
 * Pointer-release snap: bias toward toggling when the drag crosses `threshold` of the range
 * from the starting state (default `0.5` = midpoint / nearest-half).
 */
export function resolveSnapExpandT(
	currentT: number,
	expandT0: number,
	snapCommitThreshold: number,
): number {
	const th = clampSnapCommitThreshold(snapCommitThreshold);
	const hi = 1 - th;
	const lo = th;
	if (expandT0 > hi) {
		return currentT < hi ? 0 : 1;
	}
	if (expandT0 < lo) {
		return currentT > lo ? 1 : 0;
	}
	return currentT > 0.5 ? 1 : 0;
}

export interface UseSnapDragExpandOptions {
	snapPx: { c: number; e: number } | null;
	gesture: SnapDragGesture;
	/** 0 = collapsed, 1 = expanded */
	initialExpandT?: number;
	fallbackClipPx?: number;
	/**
	 * Fraction of expanded↔collapsed travel past which release commits to the other state.
	 * `0.5` = snap to nearer endpoint (legacy). `0.2` = ~20% drag from start toggles.
	 */
	snapCommitThreshold?: number;
	/** After snap completes (pointer release) */
	onAfterSnap?: () => void;
	/** Remove CSS transitions during drag (host, cross-fade targets, …) */
	getTransitionKillTargets?: () => (HTMLElement | null | undefined)[];
}

export interface UseSnapDragExpandResult {
	expandT: number;
	setExpandT: Dispatch<SetStateAction<number>>;
	maskDragging: boolean;
	/** Same as `maskDragging` but synchronous for ResizeObserver / measure guards */
	maskDraggingRef: MutableRefObject<boolean>;
	clipPx: number;
	onHandlePointerDown: (e: PointerEvent<HTMLDivElement>) => void;
	onHandlePointerMove: (e: PointerEvent<HTMLDivElement>) => void;
	onHandlePointerUp: (e: PointerEvent<HTMLDivElement>) => void;
	onHandlePointerCancel: (e: PointerEvent<HTMLDivElement>) => void;
	onLostPointerCapture: (e: PointerEvent<HTMLDivElement>) => void;
}

function isHorizontalGesture(gesture: SnapDragGesture): boolean {
	return (
		gesture === "expand-on-drag-left" || gesture === "expand-on-drag-right"
	);
}

export function useSnapDragExpand({
	snapPx,
	gesture,
	initialExpandT = 1,
	fallbackClipPx = 280,
	snapCommitThreshold = 0.5,
	onAfterSnap,
	getTransitionKillTargets,
}: UseSnapDragExpandOptions): UseSnapDragExpandResult {
	const [expandT, setExpandT] = useState(initialExpandT);
	const [maskDragging, setMaskDragging] = useState(false);
	const maskDraggingRef = useRef(false);
	const dragRef = useRef<{ x: number; y: number; expandT0: number } | null>(
		null,
	);

	const clipPx =
		snapPx != null
			? snapPx.c + (snapPx.e - snapPx.c) * expandT
			: fallbackClipPx;

	const killTransitions = useCallback(() => {
		const targets = getTransitionKillTargets?.() ?? [];
		for (const el of targets) {
			el?.style.setProperty("transition", "none");
		}
	}, [getTransitionKillTargets]);

	const restoreTransitions = useCallback(() => {
		const targets = getTransitionKillTargets?.() ?? [];
		for (const el of targets) {
			el?.style.removeProperty("transition");
		}
	}, [getTransitionKillTargets]);

	const onHandlePointerDown = useCallback(
		(e: PointerEvent<HTMLDivElement>) => {
			if (!snapPx) {
				return;
			}
			e.preventDefault();
			killTransitions();
			e.currentTarget.setPointerCapture(e.pointerId);
			maskDraggingRef.current = true;
			setMaskDragging(true);
			dragRef.current = {
				x: e.clientX,
				y: e.clientY,
				expandT0: expandT,
			};
		},
		[snapPx, expandT, killTransitions],
	);

	const onHandlePointerMove = useCallback(
		(e: PointerEvent<HTMLDivElement>) => {
			if (!maskDraggingRef.current || !snapPx || !dragRef.current) {
				return;
			}
			const range = snapPx.e - snapPx.c;
			if (range <= 0) {
				return;
			}
			const horizontal = isHorizontalGesture(gesture);
			const deltaPx = horizontal
				? e.clientX - dragRef.current.x
				: e.clientY - dragRef.current.y;
			const deltaT =
				gesture === "expand-on-drag-down" ||
				gesture === "expand-on-drag-right"
					? deltaPx / range
					: -deltaPx / range;
			setExpandT(clamp01(dragRef.current.expandT0 + deltaT));
		},
		[snapPx, gesture],
	);

	const endDrag = useCallback(
		(e: PointerEvent<HTMLDivElement>) => {
			if (e.currentTarget.hasPointerCapture(e.pointerId)) {
				e.currentTarget.releasePointerCapture(e.pointerId);
			}
			const expandT0 = dragRef.current?.expandT0 ?? expandT;
			restoreTransitions();
			maskDraggingRef.current = false;
			setMaskDragging(false);
			dragRef.current = null;
			setExpandT((t) =>
				resolveSnapExpandT(t, expandT0, snapCommitThreshold),
			);
			queueMicrotask(() => {
				onAfterSnap?.();
			});
		},
		[expandT, onAfterSnap, restoreTransitions, snapCommitThreshold],
	);

	return {
		expandT,
		setExpandT,
		maskDragging,
		maskDraggingRef,
		clipPx,
		onHandlePointerDown,
		onHandlePointerMove,
		onHandlePointerUp: endDrag,
		onHandlePointerCancel: endDrag,
		onLostPointerCapture: endDrag,
	};
}
